import { ComponentConfig } from '@/components/componentRegistry';
import { Theme } from '@mui/material/styles';
import { LoanPurpose, LoanType } from '@/config/enums';

interface ActionButtonsConfigParams {
  theme: Theme;
  t: (key: string) => string;
  selectedLoanType: string | null;
  selectedPropertyStatus: string | null;
  selectedCoBorrower: string | null;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Configuration for Action Buttons section
 * Follows responsive patterns:
 * - xs (mobile): Full width buttons, vertical stack (Next first, Back second)
 * - sm (600px+): Auto width buttons, horizontal row (Back first, Next second)
 */
export const getActionButtonsConfig = ({
  theme,
  t,
  selectedLoanType,
  selectedPropertyStatus,
  selectedCoBorrower,
  onBack,
  onNext,
}: ActionButtonsConfigParams): ComponentConfig => {
  // Validation:
  // - Purchase: require loanType + propertyAvailable + coBorrower
  // - Refinance: require loanType + coBorrower
  const isPurchaseComplete =
    selectedLoanType === LoanPurpose.Purchase &&
    selectedPropertyStatus &&
    selectedCoBorrower;
  const isRefinanceComplete =
    selectedLoanType === LoanPurpose.RefinanceCashOut && selectedCoBorrower;
  const isNextDisabled = !(isPurchaseComplete || isRefinanceComplete);

  return {
    component: 'RowLayout',
    props: {
      spacing: 'md',
      justifyContent: 'space-between',
      fullWidth: true,
      sx: {
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: theme.unit(3), sm: theme.unit(4) },
        marginTop: { xs: theme.unit(4), md: theme.unit(6) },
      },
    },
    children: [
      // Back Button
      {
        component: 'Button',
        props: {
          variant: 'outlined',
          onClick: onBack,
          sx: {
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: '120px' },
            order: { xs: 2, sm: 1 },
            height: { xs: theme.unit(12), sm: theme.unit(10) },
          },
          children: t('actions.back'),
        },
      },
      // Next Button
      {
        component: 'Button',
        props: {
          variant: 'filled',
          onClick: onNext,
          disabled: isNextDisabled,
          sx: {
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: '120px' },
            order: { xs: 1, sm: 2 },
            height: { xs: theme.unit(12), sm: theme.unit(10) },
          },
          children: t('actions.next'),
        },
      },
    ],
  };
};
